import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as MoreIcon } from '@/assets/icons/more.icon.svg';
import { useSortableData } from '@/hooks/useSortableData';
import useOnClickOutside from '@/hooks/useClickOutside';
import { motion, AnimatePresence } from 'framer-motion';
import { formatInTimeZone } from 'date-fns-tz';
import TableLoader from '@/loaders/TableLoader';
import Button from '@/components/Button';
import { usePermissions } from '@/hooks/usePermissions';
import PromptModal from '@/components/PromptModal';
import { ReactComponent as SearchIcon } from '@/assets/icons/search.icon.svg';
import { ReactComponent as CloseIcon } from '@/assets/icons/cancel.icon.svg';
import { ReactComponent as WarnIcon } from '@/assets/icons/warn.icon.svg';
import { ReactComponent as NextIcon } from '@/assets/icons/nav-right.icon.svg';
import { ReactComponent as PrevIcon } from '@/assets/icons/nav-left.icon.svg';
import { Oval } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import TableRowsSelect from '@/components/TableRowSelect';
import { deleteSurvey, fetchAllSurveys, publishSurvey } from '@/api/surveyApi';
import CreateSurveyModal from './CreateSurveyModal';
import UpdateSurveyModal from './UpdateSurveyModal';
import { useNavigate } from 'react-router-dom';
import { SearchControl } from '@/components/SearchControl';
import useDebounce from '@/hooks/useDebounce';
import { set } from 'date-fns';

const SurveyTable = () => {
  const PAGE_LIMIT = 10;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchExact, setSearchExact] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchFilters, setSearchFilters] = useState([]);
  const [search, setSearch] = useState('');
  const debouncedsearch = useDebounce(search, 500);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const ref = useRef();
  useOnClickOutside(ref, () => setShowMenu(false));

  const { hasPermission, superAdmin } = usePermissions();
  const isASuperAdmin = superAdmin(true);
  const canCreateSettings = hasPermission('settings', 'create');
  const canUpdateSettings = hasPermission('settings', 'update');
  const canDeleteSettings = hasPermission('settings', 'delete');

  const searchOptions = [
    { value: 'title', label: 'Survey Title' },
    { value: 'surveyType', label: 'Survey Type' },
    { value: 'surveyChannel', label: 'Survey Channel' }
  ];

  const handleCreate = () => {
    const isAllowed = canCreateSettings || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to create');
      return;
    } else {
      setShowCreateModal(true);
    }
  };

  const handleUpdate = (obj) => {
    const isAllowed = canUpdateSettings || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to create');
      return;
    } else {
      setSelectedItem(obj);
      setShowUpdateModal(true);
    }
  };

  const handleDelete = (obj) => {
    const isAllowed = canDeleteSettings || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to create');
      return;
    } else {
      setSelectedItem(obj);
      setShowDeleteModal(true);
    }
  };

  const handlePublish = (obj) => {
    const isAllowed = canDeleteSettings || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to create');
      return;
    } else {
      setSelectedItem(obj);
      setShowPublishModal(true);
    }
  };

  const surveys = useQuery({
    queryKey: [
      'surveys',
      {
        page,
        limit: PAGE_LIMIT,
        search: debouncedsearch,
        searchBy: searchFilters,
        searchByExactMatch: searchExact
      }
    ],
    queryFn: fetchAllSurveys
  });

  const handleDeleteSurvey = useMutation({
    mutationFn: ({ id }) => deleteSurvey(id),
    onSuccess: () => {
      setSelectedItem(null);
      setShowDeleteModal(false);
      queryClient.invalidateQueries(['surveys']);
      toast.success('Survey deleted successfully');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  const handlePublishSurvey = useMutation({
    mutationFn: ({ id }) => publishSurvey(id),
    onSuccess: () => {
      setSelectedItem(null);
      setShowPublishModal(false);
      queryClient.invalidateQueries(['surveys']);
      toast.success('This survey has been published');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  });

  const handleSearchFilter = (newFilters) => {
    setSearchFilters(newFilters);
  };

  const { items, requestSort, sortConfig } = useSortableData(surveys?.data?.data?.survey || []);

  const handleRowsPerPageChange = (value) => {
    setSearchParams({ page, limit: value });
    console.log(value);
  };

  // Fetch next page
  const fetchNextPage = () => {
    const totalPage = Math.ceil(surveys?.data?.data?.meta?.totalItems / PAGE_LIMIT);
    if (page < totalPage) {
      setPage(page + 1);
    }
  };

  // Fetch previous page
  const fetchPrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  if (surveys.isFetching && !search) {
    return <TableLoader />;
  }

  return (
    <SurveyTableView>
      <TableHeader>
        <SearchView>
          <SearchIcon />
          <input
            type="search"
            value={search}
            // ref={searchInputRef}
            placeholder="Search users"
            onChange={(e) => setSearch(e.target.value.trim())}
          />
          {surveys.isLoading && search && <Oval width={20} height={20} strokeWidth={4} />}
          <SearchControl
            onSearch={handleSearchFilter}
            currentFilters={searchFilters}
            availableFilters={searchOptions}
            searchExact={searchExact}
            onSearchExactChange={setSearchExact}
          />
        </SearchView>
        <Button
          width="20%"
          height="48px"
          type="button"
          style={{ padding: '0 20px' }}
          onClick={() => {
            handleCreate();
          }}>
          Create Survey
        </Button>
      </TableHeader>

      <TableHead>
        <div className>
          <span>Type</span>
        </div>
        <div className="type">
          <span>Title</span>
        </div>
        <div>
          <span>Channel</span>
        </div>
        <div>
          <span>Date Created</span>
        </div>
        <div className="actions"></div>
      </TableHead>
      <TableBody>
        {items.map((item, i) => {
          return (
            <div key={item?.id} className="item">
              <div>
                <span>{item?.surveyType || '-'}</span>
              </div>
              <div
                className="type"
                style={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}>
                <span>{item?.title || '-'}</span>
              </div>
              <div>
                <span>{item?.surveyChannel || '-'}</span>
              </div>
              <div>
                <span>{formatInTimeZone(new Date(item?.createdAt), 'UTC', 'do MMM, yyyy')}</span>
                <p>{formatInTimeZone(new Date(item?.createdAt), 'UTC', 'h:mm aa')}</p>
              </div>
              <div className="actions">
                <button
                  className="menu-icon"
                  onClick={() => {
                    setShowMenu(true);
                    setCurrentIndex(i);
                  }}>
                  <MoreIcon />
                </button>

                <AnimatePresence>
                  {showMenu && currentIndex === i ? (
                    <motion.div
                      className="menu-actions"
                      ref={ref}
                      initial={{ y: -20 }}
                      animate={{ y: 0 }}
                      exit={{ y: -10, opacity: 0 }}
                      transition={{ duration: 0.2 }}>
                      <button
                        onClick={() => {
                          navigate(`/settings/survey/${item?.id}`);
                        }}>
                        <span>View Responses</span>
                      </button>
                      <button
                        onClick={() => {
                          handlePublish(item);
                        }}>
                        <span>Publish Survey</span>
                      </button>
                      <button
                        onClick={() => {
                          handleUpdate(item);
                          console.log(item);
                        }}>
                        <span>Update Survey</span>
                      </button>
                      <button
                        onClick={() => {
                          handleDelete(item);
                        }}>
                        <span>Delete Survey</span>
                      </button>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>
          );
        })}
      </TableBody>

      {surveys?.data?.data?.meta?.totalItems > 0 && (
        <PaginationView>
          <div></div>
          <div className="paginate">
            <button className="prev" onClick={fetchPrevPage}>
              <PrevIcon />
            </button>
            <span>
              Page {page} of {Math.ceil(surveys?.data?.data?.meta?.totalItems / PAGE_LIMIT)}
            </span>
            <button className="next" onClick={fetchNextPage}>
              <NextIcon />
            </button>
          </div>
        </PaginationView>
      )}

      <CreateSurveyModal showModal={showCreateModal} setShowModal={setShowCreateModal} />

      <UpdateSurveyModal
        selectedItem={selectedItem}
        showModal={showUpdateModal}
        setShowModal={setShowUpdateModal}
      />

      <PromptModal showModal={showDeleteModal}>
        <div className="header">
          <button type="button" onClick={() => setShowDeleteModal(false)}>
            <CloseIcon />
          </button>
        </div>
        <div className="content">
          <WarnIcon />
          <h3>Delete Survey</h3>
          <p>Do you really want to delete this survey?</p>
          <button
            type="button"
            className="delete"
            onClick={() => handleDeleteSurvey.mutate({ id: selectedItem?.id })}>
            {handleDeleteSurvey.isLoading ? (
              <Oval color="#fff" secondaryColor="#ddd" width={30} height={30} strokeWidth={4} />
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </PromptModal>

      <PromptModal showModal={showPublishModal}>
        <div className="header">
          <button type="button" onClick={() => setShowPublishModal(false)}>
            <CloseIcon />
          </button>
        </div>
        <div className="content">
          {/* <WarnIcon /> */}
          <h3>Publish Survey</h3>
          <p>Do you want to send this survey to all users?</p>
          <button
            type="button"
            className="continue"
            onClick={() => handlePublishSurvey.mutate({ id: selectedItem?.id })}>
            {handlePublishSurvey.isLoading ? (
              <Oval color="#fff" secondaryColor="#ddd" width={30} height={30} strokeWidth={4} />
            ) : (
              'Publish'
            )}
          </button>
        </div>
      </PromptModal>
    </SurveyTableView>
  );
};

export default SurveyTable;

const SurveyTableView = styled.div`
  width: 100%;
  height: 100%;
`;

const getStatusColor = (active) => {
  switch (active) {
    case true:
      return '#16A34A';
    case 'initiated':
      return '#F4B740';
    case false:
      return '#B91C1C';

    default:
  }
};

const Status = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 0.75rem;
  text-transform: capitalize;
  color: ${({ active }) => getStatusColor(active)};

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 4px;
    background-color: ${({ active }) => getStatusColor(active)};
  }
`;

const TableView = styled.div`
  width: 100%;
  height: 100%;
`;

const TableHeader = styled.div`
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-left: none;
  border-top: none;
  border-right: none;
`;

const SearchView = styled.div`
  width: 40%;
  height: 40px;
  display: flex;
  align-items: center;

  input {
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    margin-left: 10px;
    font-weight: 400;

    &::placeholder {
      font-size: 1rem;
      font-weight: 400;
      color: ${({ theme }) => theme.colors.placeholder};
    }
  }
`;

const TableHead = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  /* border-top: 1px solid ${({ theme }) => theme.colors.line}; */

  div {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    height: 100%;

    span {
      font-size: 0.8rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.black};
    }

    .icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      width: 10px;
    }
  }

  .ascending {
    color: ${({ theme }) => theme.colors.black};

    .up path {
      stroke: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .descending {
    color: ${({ theme }) => theme.colors.black};

    .down path {
      stroke: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .amount {
    flex: 0.8;
  }

  .year {
    flex: 0.8;
  }

  .reg {
    flex: 0.6;
  }

  .tin {
    flex: 0.6;
  }

  .status {
    flex: 0.5;
  }

  .date {
    flex: 0.5;
  }

  .actions {
    flex: 0.5;
  }
`;

const TableBody = styled.div`
  width: 100%;

  .item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors?.line};

    div {
      flex: 1;
      flex-wrap: wrap;

      span {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.secondary};
        text-transform: capitalize;
      }

      p {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.secondary};
        text-transform: capitalize;
      }
    }

    .amount {
      flex: 0.8;
    }

    .year {
      flex: 0.8;
    }

    .reg {
      flex: 0.6;
    }

    .tin {
      flex: 0.6;
    }

    .status {
      flex: 0.5;

      .verified {
        color: ${({ theme }) => theme.colors?.success};
        display: flex;
        align-items: center;
        font-weight: 500;
        font-size: 0.75rem;

        .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: ${({ theme }) => theme.colors?.success};
          margin-right: 4px;
        }
      }

      .pending {
        color: ${({ theme }) => theme.colors?.warning};
        display: flex;
        align-items: center;
        font-weight: 500;
        font-size: 0.75rem;
        text-transform: capitalize;

        .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: ${({ theme }) => theme.colors?.warning};
          margin-right: 4px;
        }
      }
    }

    .date {
      flex: 0.5;
    }

    .actions {
      flex: 0.5;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      position: relative;

      svg {
        cursor: pointer;

        path {
          stroke: ${({ theme }) => theme.colors?.secondary};
        }
      }

      .menu-actions {
        position: absolute;
        top: 22px;
        right: 0px;
        background-color: ${({ theme }) => theme.colors.white};
        box-shadow:
          0px 0px 2px rgba(125, 125, 125, 0.1),
          0px 2px 10px rgba(0, 0, 0, 0.1);
        border-radius: 10px;
        z-index: 10;
        width: 180px;
        border: none;

        button {
          width: 100%;
          padding: 16px 12px;
          font-size: 0.875rem;
          background-color: transparent;
          transition: all 0.2s ease-in-out;
          border: none;
          outline: none;
          display: flex;
          justify-content: flex-start;
          align-items: center;

          &:hover {
            background-color: ${({ theme }) => theme.colors.layer3};
          }

          span {
            color: ${({ theme }) => theme.colors.activeTitle};
            width: calc(100% - 35px);
            display: flex;
            justify-content: flex-start;
            text-align: left;
            font-weight: 400;
          }
        }
      }
    }
  }
`;

const PaginationView = styled.div`
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .total {
    span {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.secondary};
    }
  }

  .paginate {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 20px;
    button {
      width: 40px;
      height: 40px;
      border: none;
      outline: none;
      background-color: ${({ theme }) => theme.colors?.layer3};
      color: ${({ theme }) => theme.colors?.secondary};
      font-size: 0.875rem;
      font-weight: 500;
      border-radius: 6px;

      &:hover {
        background-color: ${({ theme }) => theme.colors?.layer2};
      }
    }

    span {
      margin: 0 10px;
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.secondary};
    }
  }
`;
